<template>
  <div class="inquiry-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Driver Wise Students</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ recordCount }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading driver wise student report...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Driver Wise Students</span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
            <p v-if="filterSummary" class="filter-line mb-0 mt-1">
              {{ filterSummary }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="!driverBlocks.length" class="empty-state">
        <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
        <p>No students found for the selected criteria</p>
      </div>

      <section
        v-for="(block, blockIndex) in driverBlocks"
        :key="'driver-' + (block.driverID || blockIndex)"
        class="campus-block mb-4"
      >
        <div class="campus-heading">
          <div class="campus-heading__left">
            <span class="campus-heading__index">{{ blockIndex + 1 }}</span>
            <div class="campus-heading__text">
              <span class="campus-heading__name">{{ block.name || "—" }}</span>
              <div class="campus-heading__meta">
                <span v-if="block.contact">Contact: {{ block.contact }}</span>
                <span v-if="block.cnic"> · CNIC: {{ block.cnic }}</span>
                <span v-if="block.licenseNo">
                  · License: {{ block.licenseNo }}</span
                >
                <span v-if="vehicleSummary(block)">
                  · Vehicles: {{ vehicleSummary(block) }}</span
                >
              </div>
            </div>
          </div>
          <span class="campus-heading__count">
            {{ block.studentCount || (block.students || []).length }} students
          </span>
        </div>

        <div class="table-container">
          <table class="ledger-style-table">
            <thead>
              <tr>
                <th class="text-center" style="width: 4%">#</th>
                <th class="text-center" style="width: 8%">GR</th>
                <th class="text-left" style="width: 18%">NAME</th>
                <th class="text-left" style="width: 16%">FATHER NAME</th>
                <th class="text-center" style="width: 12%">CLASS / SEC</th>
                <th class="text-center" style="width: 12%">CONTACT NO</th>
                <th class="text-center" style="width: 10%">VEHICLE</th>
                <th class="text-right" style="width: 10%">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(stu, idx) in block.students || []"
                :key="(stu.sID || idx) + '-' + blockIndex"
              >
                <td class="text-center font-weight-bold">{{ idx + 1 }}</td>
                <td class="text-center gr-cell">{{ cellGr(stu) }}</td>
                <td class="text-left font-weight-bold">{{ cellText(stu.name) }}</td>
                <td class="text-left">{{ cellText(stu.father_name, stu.fatherName) }}</td>
                <td class="text-center">{{ classSectionLabel(stu) }}</td>
                <td class="text-center">{{ cellContact(stu) }}</td>
                <td class="text-center">{{ cellText(stu.vehicleNo) }}</td>
                <td class="text-right amount-cell">
                  {{ formatAmount(stu.amount) }}
                </td>
              </tr>
              <tr v-if="!(block.students || []).length">
                <td colspan="8" class="text-center py-3 text-muted">
                  No students assigned
                </td>
              </tr>
            </tbody>
            <tfoot v-if="(block.students || []).length">
              <tr class="subtotal-row">
                <td colspan="7" class="text-right font-weight-bold">
                  Driver total
                </td>
                <td class="text-right font-weight-bold amount-cell">
                  {{ formatAmount(block.totalAmount) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <div
        v-if="driverBlocks.length && reportTotalAmount != null"
        class="grand-total-bar"
      >
        <span class="font-weight-bold">Grand total</span>
        <span class="grand-total-bar__value">{{
          formatAmount(reportTotalAmount)
        }}</span>
      </div>

      <div class="page-footer">
        <small
          >Powered by MySkool.app | Generated On: {{ currentDateTime }}</small
        >
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "DriverWiseStudentsPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      driverBlocks: [],
      reportTotalAmount: null,
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: 0,
      driverID: 0,
      classID: 0,
      sectionID: 0,
      driverLabel: "",
      classLabel: "",
      sectionLabel: "",
    };
  },
  computed: {
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    filterSummary() {
      const parts = [];
      parts.push(
        this.driverLabel ||
          (this.driverID ? `Driver #${this.driverID}` : "All drivers")
      );
      parts.push(
        this.classLabel ||
          (this.classID ? `Class #${this.classID}` : "All classes")
      );
      parts.push(
        this.sectionLabel ||
          (this.sectionID ? `Section #${this.sectionID}` : "All sections")
      );
      return parts.filter(Boolean).join(" · ");
    },
    recordCount() {
      return this.driverBlocks.reduce(
        (sum, block) => sum + (block.students || []).length,
        0
      );
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = Number(query.cID) || this.$store.state.userData.cId || 0;
      this.driverID = Number(query.driverID) || 0;
      this.classID = Number(query.classID) || 0;
      this.sectionID = Number(query.sectionID) || 0;
      this.driverLabel = query.driverLabel || "";
      this.classLabel = query.classLabel || "";
      this.sectionLabel = query.sectionLabel || "";

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = this.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        console.warn("Campus info fetch failed, using fallback from store");
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const url = `${this.$store.state.domain}Driver/LoadDriverWiseReport`;
        const response = await axios.get(url, {
          params: {
            db: this.db,
            cID: this.cID,
            driverID: this.driverID,
            classID: this.classID,
            sectionID: this.sectionID,
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        const payload = response.data;
        if (!payload || String(payload.status).toLowerCase() !== "success") {
          this.error =
            payload?.message || "Failed to load driver wise student report.";
          return;
        }

        this.reportTotalAmount =
          payload.totalAmount !== undefined && payload.totalAmount !== null
            ? payload.totalAmount
            : null;
        this.driverBlocks = Array.isArray(payload.data) ? payload.data : [];
      } catch (err) {
        console.error("Driver wise students report error:", err);
        this.error = "Failed to retrieve report data.";
      } finally {
        this.loading = false;
      }
    },
    cellText(...values) {
      for (const v of values) {
        if (v === null || v === undefined) continue;
        const s = String(v).trim();
        if (s && s !== "-") return s;
      }
      return "-";
    },
    cellGr(stu) {
      return this.cellText(stu?.grNo, stu?.gr, stu?.GR);
    },
    cellContact(stu) {
      return this.cellText(
        stu?.contact_father,
        stu?.contactNo,
        stu?.contact_no,
        stu?.contact
      );
    },
    classSectionLabel(stu) {
      const cls = this.cellText(stu?.className);
      const sec = this.cellText(stu?.sectionName);
      if (cls === "-" && sec === "-") return "-";
      if (cls !== "-" && sec !== "-") return `${cls} / ${sec}`;
      return cls !== "-" ? cls : sec;
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === "") return "-";
      const n = Number(value);
      if (Number.isNaN(n)) return value;
      return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
    },
    vehicleSummary(block) {
      const vehicles = block.vehicles || [];
      if (!vehicles.length) return "";
      return vehicles
        .map((v) => {
          const no = v.vehicleNo || v.vehicle_no || "";
          const brand = v.brand ? ` (${v.brand})` : "";
          return `${no}${brand}`.trim();
        })
        .filter(Boolean)
        .join(", ");
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.inquiry-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s;
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }
  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.3);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;
  &:hover {
    color: #e74c3c;
    border-color: #fab1a0;
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
}

.report-header-modern {
  border-bottom: 3px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 100px;
    width: auto;
  }

  .school-name-full {
    font-size: 1.8rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }
  }

  .filter-line {
    font-size: 0.95rem;
    color: #636e72;
  }
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #636e72;
}

.campus-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1rem;
  margin-bottom: 0;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  page-break-after: avoid;
  page-break-inside: avoid;

  &__left {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    min-width: 0;
    flex: 1;
  }

  &__index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    padding: 0 6px;
    border-radius: 6px;
    background: #2c3e50;
    color: #fff;
    font-weight: 700;
    font-size: 0.85rem;
    flex-shrink: 0;
  }

  &__name {
    font-weight: 800;
    font-size: 1.05rem;
    color: #2c3e50;
    display: block;
  }

  &__meta {
    font-size: 0.82rem;
    color: #636e72;
    margin-top: 0.25rem;
    line-height: 1.45;
  }

  &__count {
    flex-shrink: 0;
    background: #2c3e50;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0.35rem 0.65rem;
    border-radius: 6px;
    white-space: nowrap;
  }
}

.table-container {
  border: 1px solid #dee2e6;
  border-radius: 0 0 8px 8px;
  overflow: hidden;
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 10px 12px;
    box-sizing: border-box;
    font-size: 0.9rem;
    vertical-align: middle;
  }

  thead th {
    background: #2c3e50 !important;
    color: white !important;
    font-weight: 800;
    text-transform: uppercase;
    font-size: 0.78rem;
    letter-spacing: 0.4px;
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  tfoot .subtotal-row td {
    background: #eef1f5;
    border-top: 2px solid #2c3e50;
    font-size: 0.9rem;
  }

  .gr-cell {
    font-weight: 700;
    color: #2c3e50;
  }

  .amount-cell {
    font-variant-numeric: tabular-nums;
  }
}

.grand-total-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1.5rem;
  margin-top: 1rem;
  padding: 0.85rem 1.25rem;
  background: #2c3e50;
  color: #fff;
  border-radius: 8px;
  font-size: 1rem;

  &__value {
    font-size: 1.15rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }
}

.page-footer {
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid #eee;
  color: #adb5bd;
}

@page {
  size: A4 portrait;
  margin: 10mm;
}

@media print {
  .no-print {
    display: none !important;
  }

  html,
  body {
    background: white !important;
    height: auto !important;
    overflow: visible !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .inquiry-report {
    background: white !important;
    padding: 0 !important;
    min-height: auto !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    overflow: visible !important;
  }

  .report-header-modern {
    margin-bottom: 14px !important;
    padding-bottom: 10px !important;
    border-bottom: 2px solid #000 !important;
    page-break-inside: avoid;
    break-inside: avoid;

    .school-logo-img {
      height: 80px !important;
      max-width: 180px !important;
      object-fit: contain !important;
    }

    .school-name-full {
      font-size: 1.35rem !important;
    }

    .report-info-line {
      font-size: 0.85rem !important;
    }

    .filter-line {
      font-size: 0.8rem !important;
    }
  }

  /* Let tall driver sections split between pages instead of forcing a new
     page (which left page 1 with only the report header). */
  .campus-block {
    margin-bottom: 12px !important;
    page-break-inside: auto !important;
    break-inside: auto !important;
  }

  .campus-heading {
    padding: 6px 8px !important;
    background: #f1f2f6 !important;
    border: 1px solid #000 !important;
    border-bottom: none !important;
    border-radius: 0 !important;
    page-break-after: avoid;
    break-after: avoid;
    page-break-inside: avoid;
    break-inside: avoid;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    &__index {
      background: #333 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    &__name {
      font-size: 0.9rem !important;
    }

    &__meta {
      font-size: 0.72rem !important;
    }

    &__count {
      background: #333 !important;
      color: #fff !important;
      font-size: 0.7rem !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .table-container {
    border: 1px solid #000 !important;
    border-radius: 0 !important;
    overflow: visible !important;
    page-break-inside: auto;
    break-inside: auto;
  }

  .ledger-style-table {
    font-size: 8.5pt !important;
    width: 100% !important;
    table-layout: fixed;
    page-break-inside: auto;
    break-inside: auto;

    /* Repeat column headings when a driver's table spans pages. */
    thead {
      display: table-header-group;
    }

    tbody tr,
    tfoot tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    /* Show the driver total once, at the end of the table, not on every page. */
    tfoot {
      display: table-row-group;
    }

    th,
    td {
      padding: 4px 6px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
      overflow-wrap: break-word;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    tfoot .subtotal-row td {
      background: #f5f5f5 !important;
      border-top: 2px solid #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .grand-total-bar {
    margin-top: 8px !important;
    background: #eee !important;
    color: #000 !important;
    border: 1px solid #000;
    border-radius: 0 !important;
    page-break-inside: avoid;
    break-inside: avoid;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .page-footer {
    padding-top: 10px !important;
    margin-top: 12px !important;
    color: #555 !important;
    page-break-inside: avoid;
    break-inside: avoid;
  }
}
</style>
